import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/Badge";
import { CheckCircle2, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const ACTION_LABEL = {
  started: { l: "Started", color: "orange" },
  completed: { l: "Marked ready", color: "blue" },
  approved: { l: "Approved", color: "green" },
  posted: { l: "Published", color: "green" },
  story: { l: "Stories", color: "blue" },
  task_completed: { l: "Task done", color: "green" },
};

const COUNT_TILES = [
  ["started", "Content started"],
  ["completed", "Marked ready"],
  ["approved", "Approved"],
  ["posted", "Posts published"],
  ["story", "Stories covered"],
  ["task_completed", "Tasks completed"],
];

export function DailyWork() {
  const qc = useQueryClient();
  const [checked, setChecked] = useState({});
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ["my-day"], queryFn: async () => (await api.get("/my-day")).data });

  useEffect(() => {
    if (data) {
      const init = {};
      (data.activity || []).forEach((a) => (init[a.id] = true));
      setChecked(init);
      setNote(data.note || "");
    }
  }, [data]);

  if (isLoading || !data)
    return <Card className="flex h-32 items-center justify-center rounded-lg border-zinc-200"><Loader2 className="h-5 w-5 animate-spin text-zinc-400" /></Card>;

  const activity = data.activity || [];
  const total = COUNT_TILES.reduce((s, [k]) => s + (data.counts[k] || 0), 0);

  const confirm = async () => {
    setSaving(true);
    try {
      const item_ids = Object.keys(checked).filter((k) => checked[k]);
      await api.post("/my-day/confirm", { note: note || null, item_ids });
      toast.success("Today's work confirmed 🎉");
      qc.invalidateQueries({ queryKey: ["my-day"] });
    } catch (e) {
      toast.error("Could not confirm");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="rounded-lg border-zinc-200 shadow-sm" data-testid="daily-work-card">
      <div className="flex items-center justify-between border-b border-zinc-100 px-5 py-4">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-amber-500" />
          <h3 className="font-heading text-base font-semibold">My Daily Work</h3>
        </div>
        {data.confirmed && <Badge color="green" dot data-testid="day-confirmed-badge">Confirmed today</Badge>}
      </div>

      <div className="p-5">
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
          {COUNT_TILES.map(([k, l]) => (
            <div key={k} className="rounded-md border border-zinc-100 p-3 text-center">
              <div className="metric-value text-xl font-semibold">{data.counts[k] || 0}</div>
              <div className="mt-0.5 text-[11px] leading-tight text-zinc-500">{l}</div>
            </div>
          ))}
        </div>

        {total === 0 ? (
          <div className="mt-4 py-6 text-center text-sm text-zinc-400">No activity captured yet today — your actions will appear here automatically.</div>
        ) : (
          <>
            <div className="mt-4 text-xs font-medium uppercase tracking-wide text-zinc-500">Tag today's work</div>
            <div className="mt-2 max-h-52 space-y-1.5 overflow-y-auto">
              {activity.map((a) => {
                const meta = ACTION_LABEL[a.action_type] || { l: a.action_type, color: "grey" };
                return (
                  <label key={a.id} className="flex cursor-pointer items-center gap-2.5 rounded-md border border-zinc-100 px-3 py-2 hover:bg-zinc-50" data-testid={`day-item-${a.id}`}>
                    <input type="checkbox" checked={!!checked[a.id]} onChange={(e) => setChecked((p) => ({ ...p, [a.id]: e.target.checked }))} className="h-4 w-4 rounded border-zinc-300" />
                    <Badge color={meta.color}>{meta.l}</Badge>
                    <span className="flex-1 truncate text-sm">{a.title || a.action}{a.client_name ? ` · ${a.client_name}` : ""}</span>
                    <span className="font-mono text-[11px] text-zinc-400">{(a.created_at || "").slice(11, 16)}</span>
                  </label>
                );
              })}
            </div>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Optional note (not required)…" className="mt-3" data-testid="day-note-input" />
            <Button onClick={confirm} disabled={saving} className="mt-3 w-full" data-testid="confirm-day-button">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
              Confirm Today's Work
            </Button>
          </>
        )}
      </div>
    </Card>
  );
}
